import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Datalog } from 'src/app/models/datalog';
import { SolutionSpace } from 'src/app/models/solution';
import { ResponseHandlerService } from 'src/app/services/responseHandler.service';
import { MetData } from 'src/app/shared/meteorology-chart/meteorology-chart.component';
import { MetData as AccelerationMetData } from '../acceleration-chart/acceleration-chart.component';
import { convertDateTime } from 'src/app/shared/utils';
import { DateTime } from 'luxon';
import { PolytechUiDatePickerComponent } from '../../shared/polytech-ui/date-picker/date-picker.component';
import { map, Observable } from 'rxjs';
import { DateInterval } from 'src/app/models/date.model';
import { accelerationChartComponent } from '../acceleration-chart/acceleration-chart.component';
import { MeteorologyChartComponent } from '../meteorology-chart/meteorology-chart.component';
import { PolytechUiDatePickerComponent as PolytechUiDatePickerComponent_1 } from '../polytech-ui/date-picker/date-picker.component';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgStyle } from '@angular/common';

@Component({
  selector: 'app-component-insights',
  templateUrl: './component-insights.html',
  styleUrls: ['./component-insights.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatCardModule,
    MatProgressSpinnerModule,
    PolytechUiDatePickerComponent_1,
    MeteorologyChartComponent,
    NgStyle,
    accelerationChartComponent,
  ],
})
export class ComponentInsightsComponent implements OnInit {
  @Input() solutionSpace: SolutionSpace;
  @Input() datalogsNSE$: Observable<Datalog[]>;
  @Input() datalogsHumidity$: Observable<Datalog[]>;

  @Output() updateDateRangeHumidity: EventEmitter<DateInterval> = new EventEmitter<DateInterval>();
  @Output() updateDateRangeNSE: EventEmitter<DateInterval> = new EventEmitter<DateInterval>();

  humidityMetaData: MetData[] = [];
  accelerationMetData: AccelerationMetData[] = [];
  isHumidityLoading = true;
  isNSELoading = true;
  humLabels: Array<{
    title: string;
    value: string;
    color: string;
    label: Array<{ key: string; value: string | null }>;
  }> = [];
  accelerationLegends: {
    threshold: string | number | undefined;
    highestNSE: string | number;
    aboveThres: string | number;
  } = {
    threshold: 'N/A',
    highestNSE: 'N/A',
    aboveThres: 'N/A',
  };

  @ViewChild('datePickerChockRef') datePickerChockRef: PolytechUiDatePickerComponent;
  @ViewChild('datePickerUtilRef') datePickerUtilRef: PolytechUiDatePickerComponent;

  constructor(private reponseHandler: ResponseHandlerService) {}

  ngOnInit(): void {
    this.datalogsHumidity$.pipe(map((datalogs) => this.calculateHumidityMetDataFromDatalogs(datalogs))).subscribe({
      next: (metData) => {
        this.humidityMetaData = metData;
        this.setupHumidityLabels(metData);
        this.isHumidityLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isHumidityLoading = false;
        this.reponseHandler.error(error);
      },
    });

    this.datalogsNSE$.pipe(map((datalogs) => this.calculateNSEMetDataFromDatalogs(datalogs))).subscribe({
      next: (metData) => {
        this.accelerationMetData = metData;
        this.setupAccelerationLabels(metData);
        this.isNSELoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isNSELoading = false;
        this.reponseHandler.error(error);
      },
    });
  }

  calculateHumidityMetDataFromDatalogs(datalogs: Datalog[]): MetData[] {
    return datalogs.map((datalog) => {
      const humidity =
        datalog.humidity !== null && datalog.humidity !== undefined ? Math.round(datalog.humidity) : undefined;
      const temperature =
        datalog.temperature !== null && datalog.temperature !== undefined ? Math.round(datalog.temperature) : undefined;
      const dewPoint =
        humidity !== undefined && temperature !== undefined
          ? Math.round(temperature - (100 - humidity) / 5)
          : undefined;
      return {
        date: new Date(datalog.time),
        humidity,
        temperature,
        dewPoint,
        threshold: datalog.humidityWarningValue,
        shock: undefined,
        shockThreshold: undefined,
      };
    });
  }

  calculateNSEMetDataFromDatalogs(datalogs: Datalog[]): AccelerationMetData[] {
    return datalogs.map((datalog) => ({
      date: new Date(datalog.time),
      humidity: 0,
      temperature: 0,
      dewPoint: 0,
      threshold: 0,
      schock: datalog.shock !== null && datalog.shock !== undefined ? Math.round(Math.abs(datalog.shock)) : undefined,
      shockThreshold: datalog.shockThreshold,
    }));
  }

  setupAccelerationLabels(metData: AccelerationMetData[]): void {
    this.accelerationLegends = {
      threshold: 'N/A',
      highestNSE: 'N/A',
      aboveThres: 'N/A',
    };
    if (metData && metData.length > 0) {
      const accelerationMetDataLength = metData.length;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.accelerationLegends.threshold = metData[accelerationMetDataLength - 1].shockThreshold;
      const shockValues = metData.map((item) => item.schock!).filter((val) => val !== undefined);
      this.accelerationLegends.highestNSE = shockValues.length > 0 ? Math.max(...shockValues) : 'N/A';
      this.accelerationLegends.aboveThres = metData.filter(
        (item) => item.schock !== undefined && item.shockThreshold !== undefined && item.schock > item.shockThreshold
      ).length;
    }
  }

  setupHumidityLabels(metData: MetData[]) {
    this.humLabels = [];
    const humidityValues = metData.filter((item) => item.humidity !== undefined).map((item) => item.humidity!);
    const metadataLength = humidityValues.length;
    const maxHumValue = humidityValues.length > 0 ? Math.max(...humidityValues) : 0;
    const minHumValue = humidityValues.length > 0 ? Math.min(...humidityValues) : 0;
    const aboveThresholdHum =
      metadataLength > 0
        ? metData.filter((h1) => h1.humidity != null && h1.threshold != null && h1.humidity > h1.threshold).length
        : 0;
    const humSumValues = metadataLength > 0 ? humidityValues.reduce((sum, h1) => (sum = sum + h1), 0) : 0;
    // the latest humMeasurement is the first pushed measurment in the array in the graph as chart js needs it in reverse !!!!
    const latestHumMeasurement = metadataLength > 0 ? metData[0].humidity : '';
    const latestTempMeasurement = metadataLength > 0 ? metData[0].temperature : '';
    const latestDewPointMeasurement = metadataLength > 0 ? metData[0].dewPoint : '';
    const humidityLabels: {
      title: string;
      value: string;
      color: string;
      label: Array<{ key: string; value: string | null }>;
    } = {
      title: `Humidity`,
      value: `${latestHumMeasurement} %`,

      color: '#4682B4',
      label: [
        // todo check threshold is variable
        {
          key: 'Threshold',
          value: metadataLength > 0 && metData[0].threshold != null ? metData[0].threshold.toString() + '%' : 'N/A',
        },
        {
          key: 'Times above Threshold',
          value: aboveThresholdHum.toString(),
        },
        { key: 'Highest humidity', value: metadataLength > 0 ? maxHumValue.toString() + '%' : 'N/A' },
        { key: 'Lowest humidity', value: metadataLength > 0 ? minHumValue.toString() + '%' : 'N/A' },

        {
          key: 'Average humidity',
          value: metadataLength > 0 ? Math.round(humSumValues / metadataLength).toString() + '%' : 'N/A',
        },
        {
          key: 'Humidity range',
          value: metadataLength > 0 ? (maxHumValue - minHumValue).toString() + '%' : 'N/A',
        },
      ],
    };
    const dewPointLabels: {
      title: string;
      value: string;
      color: string;
      label: Array<{ key: string; value: string | null }>;
    } = {
      title: 'Dew Point',
      value: `${latestDewPointMeasurement} °C`,
      color: '#028767',

      label: [],
    };
    const temperatureValues = metData.filter((item) => item.temperature !== undefined).map((item) => item.temperature!);
    const TempMetadataLength = temperatureValues.length;
    const maxTempValue = temperatureValues.length > 0 ? Math.max(...temperatureValues) : 0;
    const minTempValue = temperatureValues.length > 0 ? Math.min(...temperatureValues) : 0;
    const TempSumValues = temperatureValues.length > 0 ? temperatureValues.reduce((sum, v) => sum + v, 0) : 0;
    const temoperatureLabels: {
      title: string;
      value: string;
      color: string;
      label: Array<{ key: string; value: string | null }>;
    } = {
      title: `Temperature`,
      value: `${latestTempMeasurement} °C`,
      color: '#dc2d0a',

      label: [
        { key: 'Highest measurement', value: TempMetadataLength > 0 ? maxTempValue.toString() + '°C' : 'N/A' },
        { key: 'Lowest measurement', value: TempMetadataLength > 0 ? minTempValue.toString() + '°C' : 'N/A' },
        {
          key: 'Average humidity',
          value: TempMetadataLength > 0 ? Math.round(TempSumValues / TempMetadataLength).toString() + '°C' : 'N/A',
        },
        {
          key: 'Humidity range',
          value: TempMetadataLength > 0 ? (maxTempValue - minTempValue).toString() + '°C' : 'N/A',
        },
      ],
    };

    this.humLabels.push(humidityLabels);
    this.humLabels.push(temoperatureLabels);
    this.humLabels.push(dewPointLabels);
  }

  updateDateRange(evt: { start: number; end: number }, type: 'ACCELERATION' | 'HUMIDITY'): void {
    switch (type) {
      case 'ACCELERATION':
        this.isNSELoading = true;
        this.updateDateRangeNSE.emit({
          start: convertDateTime(evt.start),
          end: convertDateTime(evt.end, 1),
        });
        break;
      case 'HUMIDITY':
        this.isHumidityLoading = true;
        this.updateDateRangeHumidity.emit({
          start: convertDateTime(evt.start),
          end: convertDateTime(evt.end, 1),
        });
        break;
    }
  }

  selectAllData(): void {
    const startDate: DateTime = DateTime.fromISO('2020-01-01', { setZone: true, zone: 'Africa/Abidjan' });

    try {
      this.datePickerUtilRef.setStartDate(startDate.toJSDate());
      this.datePickerUtilRef.resetCalendars();
    } catch (e: any) {
      /* stub */
    }
    try {
      this.datePickerChockRef.setStartDate(startDate.toJSDate());
      this.datePickerChockRef.resetCalendars();
    } catch (e: any) {
      /* stub */
    }
  }
}
